import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuthActions } from "@convex-dev/auth/react";
import { toast } from "sonner";
import {
  BellIcon,
  ChartPieSliceIcon,
  CreditCardIcon,
  EnvelopeSimpleIcon,
  GearIcon,
  ListIcon,
  MoonIcon,
  SignOutIcon,
  SunIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useTheme } from "../../context/ThemeContext";
import { useLocalization } from "../../context/LocalizationContext";

const MAIN_ITEMS = [
  {
    to: "/",
    labelKey: "navigation.overview",
    icon: ChartPieSliceIcon,
    testId: "global-nav-overview",
    end: true,
  },
];

const SETTINGS_ITEMS = [
  {
    to: "/settings/imports",
    labelKey: "navigation.emailImports",
    icon: EnvelopeSimpleIcon,
    testId: "global-nav-settings-imports",
  },
  {
    to: "/settings/notifications",
    labelKey: "navigation.notifications",
    icon: BellIcon,
    testId: "global-nav-settings-notifications",
  },
];

const navItemClass = ({ isActive }) =>
  [
    "flex items-center gap-3 border px-4 py-3 rounded-none text-sm font-medium",
    isActive
      ? "border-[#002FA7]/20 bg-[#002FA7]/10 text-[#001B61] dark:border-blue-500/40 dark:bg-zinc-900 dark:text-blue-100"
      : "border-zinc-200 bg-white text-zinc-900 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700",
  ].join(" ");

const AppShell = () => {
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLocalization();
  const { signOut } = useAuthActions();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const settingsActive = location.pathname.startsWith("/settings");

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await signOut();
    toast.success(t("navigation.loggedOut"));
  };

  const renderNavContent = ({ mobile = false } = {}) => (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-zinc-200 pb-4 dark:border-zinc-700">
        <div className="flex items-center gap-3">
          <CreditCardIcon
            size={24}
            className="text-[#002FA7] dark:text-blue-400"
            weight="regular"
          />
          <div>
            <p className="font-outfit text-xl tracking-tight text-zinc-900 dark:text-zinc-100">
              Submind
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="global-theme-toggle"
            onClick={toggleTheme}
            className="flex items-center justify-center rounded-none border border-zinc-200 bg-white p-2 text-zinc-900 hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-700 dark:text-zinc-100 dark:hover:bg-zinc-600"
            aria-label={t("navigation.toggleTheme")}
          >
            {theme === "light" ? (
              <MoonIcon size={18} weight="regular" />
            ) : (
              <SunIcon size={18} weight="regular" />
            )}
          </button>
          {mobile ? (
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center rounded-none border border-zinc-200 bg-white p-2 text-zinc-900 hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-700 dark:text-zinc-100 dark:hover:bg-zinc-600"
              aria-label={t("navigation.closeNavigation")}
            >
              <XIcon size={18} weight="regular" />
            </button>
          ) : null}
        </div>
      </div>

      <div className="mt-5 flex-1 space-y-5 overflow-y-auto">
        <nav className="grid gap-3">
          {MAIN_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testId}
                className={navItemClass}
              >
                <Icon size={18} weight="regular" />
                <span>{t(item.labelKey)}</span>
              </NavLink>
            );
          })}
        </nav>

        <div>
          <div
            className={`flex items-center gap-3 rounded-none border px-4 py-3 ${
              settingsActive
                ? "border-[#002FA7]/20 bg-[#002FA7]/10 text-[#001B61] dark:border-blue-500/40 dark:bg-zinc-900 dark:text-blue-100"
                : "border-zinc-200 bg-white text-zinc-900 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
            }`}
          >
            <GearIcon size={18} weight="regular" />
            <div>
              <p className="text-sm font-medium">{t("navigation.settings")}</p>
            </div>
          </div>
          <nav className="mt-3 grid gap-2 border-l border-zinc-200 pl-3 dark:border-zinc-700">
            {SETTINGS_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId}
                  className={navItemClass}
                >
                  <Icon size={18} weight="regular" />
                  <span>{t(item.labelKey)}</span>
                </NavLink>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="mt-6 border-t border-zinc-200 pt-5 dark:border-zinc-700">
        <button
          data-testid="global-logout-button"
          onClick={handleLogout}
          className="flex w-full items-center justify-center gap-2 rounded-none border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-900 hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-700 dark:text-zinc-100 dark:hover:bg-zinc-600"
        >
          <SignOutIcon size={16} weight="regular" />
          {t("navigation.signOut")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-900">
      <div className="sticky top-0 z-40 flex items-center justify-between border-b border-zinc-200 bg-[#FAFAFA]/95 px-4 py-4 backdrop-blur-sm dark:border-zinc-700 dark:bg-zinc-900/95 lg:hidden">
        <div className="flex items-center gap-3">
          <CreditCardIcon
            size={22}
            className="text-[#002FA7] dark:text-blue-400"
            weight="regular"
          />
          <div>
            <p className="font-outfit text-lg tracking-tight text-zinc-900 dark:text-zinc-100">
              Submind
            </p>
          </div>
        </div>
        <button
          type="button"
          data-testid="open-mobile-nav"
          onClick={() => setMobileMenuOpen(true)}
          className="flex items-center justify-center rounded-none border border-zinc-200 bg-white p-2 text-zinc-900 hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700"
          aria-label={t("navigation.openNavigation")}
          aria-expanded={mobileMenuOpen}
        >
          <ListIcon size={18} weight="regular" />
        </button>
      </div>

      <div className="flex min-h-screen">
        <div
          className={`fixed inset-0 z-50 lg:hidden ${
            mobileMenuOpen ? "pointer-events-auto" : "pointer-events-none"
          }`}
          aria-hidden={!mobileMenuOpen}
        >
          <button
            type="button"
            onClick={() => setMobileMenuOpen(false)}
            className={`absolute inset-0 bg-zinc-950/45 ${
              mobileMenuOpen ? "opacity-100" : "opacity-0"
            }`}
            aria-label={t("navigation.closeOverlay")}
          />
          <aside
            className={`relative h-full w-[88vw] max-w-[320px] border-r border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-800 ${
              mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <div className="h-full p-4 sm:p-5">
              {renderNavContent({ mobile: true })}
            </div>
          </aside>
        </div>

        <aside className="hidden min-h-screen w-[296px] shrink-0 border-r border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-800 lg:block">
          <div className="sticky top-0 h-screen p-5">{renderNavContent()}</div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="w-full px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
            <div className="mx-auto w-full max-w-7xl">
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppShell;
